import {
  InvoicePaymentStatus,
  InvoiceStage,
  InvoiceStatus,
  type IBaseInvoice,
} from 'src/types/invoices';

import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';

import { Iconify } from 'src/components/iconify';
import { useTranslate } from 'src/locales';
import { Box, Link, ListItemText } from '@mui/material';
import { fDate, fIsBeforeToday } from 'src/utils/format-time';
import { fCurrency } from 'src/utils/format-number';
import { Label } from 'src/components/label';

import UserAvatar from 'src/components/user-avatar/UserAvatar';
import CountryWithFlag from 'src/components/country-with-flag/CountryWithFlag';
import ShortenCode from 'src/components/shorten-code/ShortenCode';

type OwnProps = {
  row: IBaseInvoice;
  selected: boolean;
  onEditRow: () => void;
  rowIndex: number;
};

export function InvoicesGridRow({ row, selected, onEditRow, rowIndex }: OwnProps) {
  const { t } = useTranslate();

  return (
    <TableRow hover selected={selected} data-test-id={`row-${rowIndex}`}>
      <TableCell>
        <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
          <UserAvatar
            dataTestId="customer-link"
            name={row.customer.customerName}
            subText={row.customer.customerNo}
            to={row.customer.id}
          />
        </Box>
      </TableCell>

      <TableCell>
        <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
          <ListItemText
            disableTypography
            primary={
              <Link
                data-test-id="invoice-no-link"
                onClick={onEditRow}
                variant="body2"
                sx={{
                  cursor: 'pointer',
                  color: 'warning.contrastText',
                  textDecoration: 'underline',
                  '&:hover': { color: 'warning.contrastText', textDecoration: 'underline' },
                }}
              >
                <ShortenCode value={row.invoiceNo} maxLength={15} typography="body2" />
              </Link>
            }
          />
        </Box>
      </TableCell>

      <TableCell>
        <CountryWithFlag countryCode={row.countryCode} />
      </TableCell>

      <TableCell>
        <ListItemText
          primary={fDate(row.dueDate)}
          primaryTypographyProps={{
            typography: 'body2',
            noWrap: true,
            sx: { color: fIsBeforeToday(row.dueDate) ? 'red' : '' },
          }}
        />
      </TableCell>

      <TableCell align="right" sx={{ fontWeight: 600 }}>
        {fCurrency(row.totalRemainingBalance, {
          minimumFractionDigits: 2,
          currency: row.currency || '',
        })}
      </TableCell>

      <TableCell align="center">
        <Label
          variant="soft"
          color={
            (row.stage === InvoiceStage.Invoiced && 'info') ||
            (row.stage === InvoiceStage.Reminded && 'warning') ||
            (row.stage === InvoiceStage.DebtCollection && 'error') ||
            'default'
          }
          sx={{ width: 100 }}
        >
          {t(`pages.content.invoiceStages.${row.stage}`)}
        </Label>
      </TableCell>

      <TableCell align="center">
        <Label
          variant="filled"
          color={
            (row.status === InvoiceStatus.Open && 'success') ||
            (row.status === InvoiceStatus.Closed && 'default') ||
            (row.status === InvoiceStatus.Cancelled && 'error') ||
            (row.status === InvoiceStatus.Paused && 'warning') ||
            'default'
          }
          sx={{ width: 70 }}
        >
          {t(`pages.content.invoiceStatuses.${row.status}`)}
        </Label>
      </TableCell>

      <TableCell align="center">
        <Label
          variant="outlined"
          color={
            (row.paymentStatus === InvoicePaymentStatus.Paid && 'success') ||
            (row.paymentStatus === InvoicePaymentStatus.NotPaid && 'error') ||
            (row.paymentStatus === InvoicePaymentStatus.PartiallyPaid && 'warning') ||
            (row.paymentStatus === InvoicePaymentStatus.CapitalPaid && 'secondary') ||
            'default'
          }
          sx={{ width: 100 }}
        >
          {t(`pages.content.invoicePaymentStatuses.${row.paymentStatus}`)}
        </Label>
      </TableCell>

      <TableCell align="right" sx={{ px: 1 }}>
        <IconButton color="default" onClick={onEditRow} data-test-id="edit-row-link">
          <Iconify icon="lucide:eye" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
